import { useCallback, useEffect, useRef, useState } from 'react';

type Options = {
  src: string;
  volume?: number; // 0..1
};

export function useTickSound({ src, volume = 1 }: Options) {
  const ctxRef = useRef<AudioContext | null>(null);
  const bufRef = useRef<AudioBuffer | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const [ready, setReady] = useState(false);

  // Инициализация по первому юзер-жесту (touchstart)
  const init = useCallback(async () => {
    if (ctxRef.current) return;
    const ctx = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    ctxRef.current = ctx;
    gainRef.current = ctx.createGain();
    gainRef.current.gain.value = volume;
    gainRef.current.connect(ctx.destination);

    const resp = await fetch(src);
    const arr = await resp.arrayBuffer();
    bufRef.current = await ctx.decodeAudioData(arr);

    // На iOS может быть suspended — на всякий случай резюмнём
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {}
    }

    setReady(true);
  }, [src, volume]);

  // Одиночный тик (короткий)
  const playOnce = useCallback((detuneCents = 0) => {
    const ctx = ctxRef.current;
    const buf = bufRef.current;
    const gain = gainRef.current;
    if (!ctx || !buf || !gain) return;

    const srcNode = ctx.createBufferSource();
    srcNode.buffer = buf;
    // лёгкая вариативность высоты, чтобы не «робот»
    try {
      (srcNode as any).detune?.setValueAtTime(detuneCents, ctx.currentTime);
    } catch {}

    srcNode.connect(gain);
    srcNode.start();
  }, []);

  // Сыграть N тиков равномерно за durationMs
  const playSteps = useCallback((steps: number, durationMs: number) => {
    const ctx = ctxRef.current;
    if (!ctx || !bufRef.current || steps <= 0) return;

    const interval = durationMs / steps;
    for (let i = 0; i < steps; i++) {
      const when = ctx.currentTime + (interval * i) / 1000;
      const srcNode = ctx.createBufferSource();
      srcNode.buffer = bufRef.current!;
      // лёгкая рандомизация тона в пределах ±10 центов
      const cents = Math.random() * 20 - 10;
      try {
        (srcNode as any).detune?.setValueAtTime(cents, when);
      } catch {}
      srcNode.connect(gainRef.current!);
      srcNode.start(when);
    }
  }, []);

  useEffect(() => {
    return () => {
      try {
        ctxRef.current?.close();
      } catch {}
      ctxRef.current = null;
      bufRef.current = null;
      gainRef.current = null;
    };
  }, []);

  return { ready, init, playOnce, playSteps };
}
